/**
 * The rate card, as data — and the one description of what "price this" means.
 *
 * `.docs/10-catalog.md` §DRY watchlist: "The `computePrice` input union is one zod
 * schema in `packages/shared/schemas/catalog.ts`; the action, the client route and
 * (later) the SPA's package builder use it." Everything below is that, plus the
 * shape of the card itself — which is what `fes://catalog/rate-card` serves, what
 * `catalog_list_*` answers with, and what `GET /api/catalog` renders at one client's
 * tier.
 *
 * **Codes, not ids, and not enums.** Every row in the catalog is addressed by its
 * `code` (`ARCHITECTURE.md` §Naming: "Every tool addresses entities by human ref,
 * never UUID"). They are *not* a closed union here, deliberately: the catalog is
 * data — that is the whole point of the module, and `.docs/10-catalog.md` says so —
 * so a `z.enum` in this file would be a second list of services beside the seed, and
 * the second list is the one that drifts. An unknown code is a `NotFound` from the
 * service naming what it looked for, which is a better answer than a schema error
 * anyway.
 *
 * **Field names are snake_case** because this file describes what goes over the
 * wire: MCP tool inputs and outputs, the JSON resource, and the REST body
 * (`ARCHITECTURE.md` §API conventions). `catalog/service.ts` maps the database rows
 * onto these once.
 */
import { z } from 'zod';
import { CONTENT_TYPE_CODES, TIER_CODES } from '../pricing.js';
import { CurrencySchema, DateOnlySchema, MoneySchema } from './primitives.js';
/* ------------------------------------------------------------------- the codes */
/**
 * A catalog code: `seo_content`, `website_m`, `odoo_accounting`, `chatbot`.
 *
 * Bounded and snake_case, so a code can go in a URL, a `CHECK` constraint and an
 * audit row without escaping. The length cap is what stops a caller pushing a
 * paragraph through a field the service will `NotFound` anyway.
 */
export const CATALOG_CODE_PATTERN = /^[a-z][a-z0-9_]{1,48}[a-z0-9]$/;
export const CatalogCodeSchema = z
    .string()
    .regex(CATALOG_CODE_PATTERN, 'A catalog code is lowercase words joined by underscores.')
    .describe('A catalog code — "seo_content", "website_m", "odoo_accounting". Lowercase words joined ' +
    'by underscores. catalog_list_services, catalog_list_project_templates and ' +
    'catalog_list_widget_types are where the codes come from.');
export const TierCodeSchema = z
    .literal(TIER_CODES)
    .describe('Market tier: 1 (US, Canada, UK, Australia, W/N Europe, Gulf — ×1.0), 2 (S/E Europe, ' +
    'Chile, Mexico, Malaysia — ×0.7) or 3 (Turkey, the Balkans, LATAM, SE Asia, N Africa ' +
    '— ×0.5). It follows the market the work targets, not where the client is ' +
    'incorporated (PRICING.md).');
export const SERVICE_CATEGORIES = ['project', 'monthly', 'usage'];
export const ServiceCategorySchema = z
    .enum(SERVICE_CATEGORIES)
    .describe('How the service is sold: monthly (a retainer block with an allowance), project (a ' +
    'quote with milestones) or usage (a widget, billed per event). SERVICES.md, Catalog.');
export const PROJECT_SIZES = ['S', 'M', 'L', 'fixed'];
export const ProjectSizeSchema = z
    .enum(PROJECT_SIZES)
    .describe('Template size: S, M or L on the project grid, or "fixed" for something priced as one ' +
    'thing — the Odoo Foundation, a takeover audit, a discovery.');
/**
 * The portal modules that can **host** a kind of widget (session 46).
 *
 * Every widget before session 46 was a separate service metering into the portal
 * (`ARCHITECTURE.md` §Widgets: "Widgets themselves are separate services"). The blog
 * and the forms are not: their backend is a portal module, and `widget_type.hosted_by`
 * names which one. Null is the ordinary case — a widget that runs somewhere else and
 * speaks `/api/v1/usage` — and stays the ordinary case.
 *
 * The value is the **surface**: which module serves the widget's delivery API and which
 * SPA workspace edits it. A widget snapshots it at creation, like its price, so a live
 * widget's surface never moves under it.
 *
 * Here rather than in `usage.ts` because `widget_type` is the row that declares it and
 * `usage.ts` already imports from this file; the reverse would be a cycle.
 */
export const HOSTED_SURFACES = ['blog', 'forms'];
export const HostedSurfaceSchema = z
    .enum(HOSTED_SURFACES)
    .describe('The portal module that hosts this kind of widget: "blog" — posts written and published ' +
    'in the portal and read by the site through the delivery API; "forms" — submissions ' +
    'the portal stores and forwards. Null for a widget that runs as its own service and ' +
    'meters through the usage API.');
/**
 * How an add-on is priced, and therefore whether it can be priced at all.
 *
 * Only `fixed` and `percent` reach the engine. The other three are on the rate card
 * because `client/billing.html` prints them — "collateral item $80 – $250", "Custom
 * API integration from $2,000", "Payroll — quote" — and a catalogue that silently
 * turned any of them into a number would be quoting a price nobody agreed.
 */
export const ADD_ON_PRICING = ['fixed', 'percent', 'range', 'from', 'quote'];
export const AddOnPricingSchema = z
    .enum(ADD_ON_PRICING)
    .describe('fixed — one amount, bought a whole number of times. percent — a share of the ' +
    'template amount ("extra language +25%"). range — a band, quoted per item. from — a ' +
    'floor, quoted. quote — no published price. Only fixed and percent can be computed; ' +
    'the other three make catalog_compute_price refuse and say so.');
/* -------------------------------------------------------------- what to price */
export const PackageItemSchema = z
    .strictObject({
    service: CatalogCodeSchema,
    blocks: z
        .int()
        .min(1)
        .max(50)
        .describe('How many standard blocks of it — 2 blocks of SEO content is 8 articles a month. ' +
        'One is the minimum; a service is never in a package twice.'),
})
    .describe('One line of a package: a monthly service and how many of its blocks.');
/**
 * One fixed line on a retainer, outside the rate card (`PRICING.md` §Custom lines).
 *
 * The amount is in the client's **billing currency**, unlike a block amount, which
 * is USD at Tier 1: a custom line is what one client agreed to pay, and the engine
 * adds it to the total exactly as given — no bundle discount, no market tier, no
 * conversion, no rounding, and it is not a block for the discount on the others.
 */
export const CustomLineSchema = z
    .strictObject({
    name: z
        .string()
        .trim()
        .min(2)
        .max(80)
        .describe('What the line reads as on every invoice — "Service Schedule No. 1". Named once ' +
        'per package, case-insensitively.'),
    scope: z
        .string()
        .trim()
        .max(500)
        .default('')
        .describe('What the fee covers, in the client’s own words. It is snapshotted onto the retainer ' +
        'exactly as a block’s scope is. Left out, blank.'),
    amount: MoneySchema.describe('The agreed amount per month, in the client’s billing currency, in minor units — ' +
        '35000 is $350.00. It is charged exactly as given: no bundle discount, no market ' +
        'tier, no conversion and no rounding touch it, and it does not count as a block ' +
        'for the discount on the others. Positive; a credit is a discount line on an invoice.'),
})
    .describe('One fixed line on a retainer: work agreed with one client at one price, outside the ' +
    'rate card.');
const ProjectAddOnSchema = z
    .strictObject({
    code: CatalogCodeSchema,
    qty: z
        .int()
        .min(1)
        .max(100)
        .default(1)
        .describe('How many of it — 2 extra pages, 2 standard connectors. Always 1 for a percentage ' +
        'add-on, which is a share of the template and not something bought twice.'),
})
    .describe('One add-on on a project quote, and how many of it.');
/**
 * What is being priced, without saying for whom. Four shapes, one discriminator.
 *
 * `tier` and `currency` are deliberately **not** in here: they are the client's, and
 * `GET /api/catalog`'s sibling takes them from the client's own row rather than from
 * the body, so a client cannot ask to be shown a price at a tier they are not on.
 * `ComputePriceInputSchema` below is this plus those two, and is what the studio tool
 * takes.
 */
export const PriceRequestSchema = z
    .discriminatedUnion('kind', [
    z
        .strictObject({
        kind: z.literal('monthly').describe('Price a package of monthly blocks.'),
        items: z
            .array(PackageItemSchema)
            .max(30)
            .default([])
            .describe('The blocks in the package. The minimum retainer is one block or one custom ' +
            'line (PRICING.md). Social content Max replaces Base and Odoo Plus replaces ' +
            'Odoo Base; asking for both of a pair is refused.'),
        custom_lines: z
            .array(CustomLineSchema)
            .max(10)
            .default([])
            .describe('Fixed lines outside the rate card, added to the total exactly as given ' +
            '(PRICING.md, Custom lines). Left out, none.'),
    })
        .describe('A retainer package: blocks, the bundle discount, the tier, rounded to $10, plus ' +
        'any custom lines as given.'),
    z
        .strictObject({
        kind: z.literal('extra').describe('Price work past an allowance, or a rate-card item.'),
        service: CatalogCodeSchema,
        unit: z
            .string()
            .regex(CATALOG_CODE_PATTERN, 'An extra unit code is lowercase words joined by _.')
            .describe('Which unit of that service — "article", "hour", "edit", "channel", ' +
            '"spend_band", "platform", "reel", "carousel". catalog_list_services lists ' +
            'each service’s units. A service’s own **fixed add-on** is also a unit here: ' +
            'a post-launch website change is an extra priced from the website add-on card, ' +
            'so "website_extra_page" and "website_copywriting" are accepted for "website".'),
        qty: z
            .number()
            .min(0)
            .max(100_000)
            .describe('How many units. May be fractional where the unit is — 6.5 Odoo hours. The ' +
            'unit price is rounded to $5 first and then multiplied, which is the order ' +
            'PRICING.md gives.'),
    })
        .describe('An extra: the unit at this tier, rounded to $5, times the quantity.'),
    z
        .strictObject({
        kind: z.literal('project').describe('Price a project quote from a template and add-ons.'),
        template: CatalogCodeSchema,
        add_ons: z
            .array(ProjectAddOnSchema)
            .max(50)
            .default([])
            .describe('The add-ons on top of the template, in the order they should read on the ' +
            'quote. Left out, just the template.'),
        retainer_client: z
            .boolean()
            .default(false)
            .describe('True when the project is signed alongside a retainer, which takes 10% off the ' +
            'project (PRICING.md, Projects). It never discounts the retainer.'),
    })
        .describe('A project: template plus add-ons, the tier, the retainer discount, to $10.'),
    z
        .strictObject({
        kind: z
            .literal('package_change')
            .describe('Price a change to a package: what it costs now, and what it would cost.'),
        current: z
            .array(PackageItemSchema)
            .max(30)
            .default([])
            .describe('The blocks the client is on today. Empty for a retainer that is custom lines ' +
            'only.'),
        current_custom_lines: z
            .array(CustomLineSchema)
            .max(10)
            .default([])
            .describe('The custom lines the client is on today. Left out, none.'),
        proposed: z
            .array(PackageItemSchema)
            .max(30)
            .default([])
            .describe('The blocks after the change. The whole package, not the difference — the ' +
            'discount and the rounding apply to the new total, not to each change on ' +
            'its own.'),
        proposed_custom_lines: z
            .array(CustomLineSchema)
            .max(10)
            .default([])
            .describe('The custom lines after the change — again the whole set, not the difference. ' +
            'A line left out is a line dropped.'),
    })
        .describe('Two packages and the difference between them, as a package-change quote.'),
])
    .describe('What to price. The "kind" field decides which of the other fields are read.');
/** The studio tool's input: what to price, and for which market and currency. */
export const ComputePriceInputSchema = z
    .strictObject({
    request: PriceRequestSchema,
    tier: TierCodeSchema,
    currency: CurrencySchema,
})
    .describe('A hypothetical price. Nothing is written and nothing is committed.');
/** The client portal's body: what to price. The tier and currency are the client's. */
export const ClientPriceRequestSchema = z
    .strictObject({ request: PriceRequestSchema })
    .describe('What to price, at this client’s own market tier and billing currency.');
/* --------------------------------------------------------------- the answers */
const TierStepSchema = z
    .object({
    code: TierCodeSchema,
    multiplier_bps: z
        .int()
        .describe('The multiplier as basis points: 10000, 7000 or 5000 for tiers 1, 2 and 3.'),
    amount: MoneySchema.describe('USD, after the tier and before the billing currency.'),
})
    .describe('The market-tier step of a breakdown.');
/**
 * The monthly build-up, in the order `client/billing.html` prints it.
 *
 * Every amount up to `converted` is USD; `converted`, `rounding` and `total` are in
 * the billing currency. `rounding` is the `+$1.00` line the mockup shows only when it
 * is non-zero.
 */
export const MonthlyBreakdownSchema = z
    .object({
    currency: CurrencySchema,
    block_count: z
        .int()
        .describe('Σ blocks. Social content Max counts as one block, exactly as Base does.'),
    list: MoneySchema.describe('Σ block amount × blocks, USD, before anything comes off.'),
    bundle_discount_bps: z
        .int()
        .describe('The band reached: 0, 500, 1000 or 1500 basis points (PRICING.md).'),
    bundle_discount: MoneySchema.describe('Negative or zero, USD.'),
    after_discount: MoneySchema.describe('USD, list plus the discount.'),
    tier: TierStepSchema,
    converted: MoneySchema.nullable().describe('The billing-currency amount before rounding, or null when billing in USD.'),
    rounding: MoneySchema.describe('What rounding to $10 added or removed from the blocks. Shown as its own line only ' +
        'when non-zero.'),
    blocks_total: MoneySchema.describe('The blocks alone, after the rounding, in the billing currency — what total is before ' +
        'the custom lines are added.'),
    custom_lines: z
        .array(z
        .object({
        name: z.string().describe('What the line reads as on the invoice.'),
        amount: MoneySchema.describe('The agreed amount per month, in the billing currency, exactly as given.'),
    })
        .describe('One fixed line, as agreed.'))
        .describe('The fixed lines outside the rate card, in the order they were given. Empty for a ' +
        'package built from the card alone.'),
    custom_total: MoneySchema.describe('Σ custom lines, in the billing currency. Zero when there are none.'),
    total: MoneySchema.describe('What is charged every month, in the billing currency: blocks_total plus custom_total.'),
})
    .describe('How a monthly amount is arrived at, step by step.');
export const ProjectBreakdownSchema = z
    .object({
    currency: CurrencySchema,
    template: MoneySchema.describe('The template amount, USD, Tier 1 list.'),
    add_ons: z
        .array(z
        .object({
        code: CatalogCodeSchema,
        name: z.string().describe('What it reads as on the quote.'),
        amount: MoneySchema.describe('USD list, already multiplied by the quantity.'),
    })
        .describe('One resolved add-on line.'))
        .describe('Each add-on as a USD amount, in the order it was given.'),
    subtotal: MoneySchema.describe('USD, template plus add-ons — the quote’s "Subtotal" row.'),
    tier: TierStepSchema,
    retainer_discount_bps: z
        .int()
        .describe('1000 when the project is signed alongside a retainer, otherwise 0.'),
    retainer_discount: MoneySchema.describe('Negative or zero.'),
    after_discount: MoneySchema,
    converted: MoneySchema.nullable().describe('Null when billing in USD.'),
    rounding: MoneySchema.describe('What rounding to $10 added or removed.'),
    total: MoneySchema.describe('The quoted total, in the billing currency.'),
})
    .describe('How a project quote is arrived at, step by step.');
/**
 * What `catalog_compute_price` answers with: one object whose `kind` says which of
 * the four it is.
 *
 * A single object rather than a union, because MCP validates every tool's output
 * against `{ type: 'object' }` and a caller that has to branch on `kind` anyway is
 * better served by fields it can read than by a shape it has to narrow. The unread
 * fields are null.
 */
export const ComputedPriceSchema = z
    .object({
    kind: z
        .enum(['monthly', 'extra', 'project', 'package_change'])
        .describe('Which of the four was asked for. It decides which fields below are filled.'),
    currency: CurrencySchema,
    tier: TierCodeSchema,
    total: MoneySchema.describe('The answer: the monthly amount, the extra’s line total, the quoted total, or the ' +
        'proposed package’s monthly amount.'),
    monthly: MonthlyBreakdownSchema.nullable().describe('The build-up for kind "monthly", or the proposed one for "package_change". Null ' +
        'otherwise.'),
    project: ProjectBreakdownSchema.nullable().describe('The build-up for kind "project". Null otherwise.'),
    extra: z
        .object({
        service: CatalogCodeSchema,
        unit: z.string().describe('The unit code that was priced.'),
        noun: z.string().describe('What one of them is called — "article", "hour", "reel".'),
        unit_amount: MoneySchema.describe('One unit at this tier, rounded to $5. This is what an invoice line carries.'),
        qty: z.number().describe('How many were priced.'),
        amount: MoneySchema.describe('unit_amount × qty, exactly.'),
    })
        .nullable()
        .describe('The line for kind "extra". Null otherwise.'),
    change: z
        .object({
        current: MonthlyBreakdownSchema.describe('What the package costs today.'),
        proposed: MonthlyBreakdownSchema.describe('What it would cost.'),
        delta: MoneySchema.describe('proposed.total − current.total. Positive is more a month, negative is less.'),
        lines: z
            .array(z
            .object({
            service: CatalogCodeSchema,
            name: z.string().describe('The service, as it reads on the table.'),
            blocks_now: z.int().describe('Blocks today. Zero when it is being added.'),
            blocks_proposed: z.int().describe('Blocks after. Zero when it is being dropped.'),
            amount_now: MoneySchema.describe('Its USD list line today.'),
            amount_proposed: MoneySchema.describe('Its USD list line after.'),
        })
            .describe('One row of the block-by-block table.'))
            .describe('Every service in either package, block by block — the table on ' +
            'client/requests.html. USD list amounts, before the discount and the tier.'),
        custom_lines: z
            .array(z
            .object({
            name: z.string().describe('The line, as it reads on the invoice.'),
            amount_now: MoneySchema.nullable().describe('Its amount today, in the billing currency, or null when it is being added.'),
            amount_proposed: MoneySchema.nullable().describe('Its amount after, in the billing currency, or null when it is being dropped.'),
        })
            .describe('One custom line in either package.'))
            .describe('Every custom line in either package, by name — current ones first, in their ' +
            'order, then the new ones. Billing-currency amounts, exactly as agreed.'),
    })
        .nullable()
        .describe('The two breakdowns and the difference, for kind "package_change". Null else.'),
})
    .describe('A computed price and the steps behind it. Nothing was written. Anything with no ' +
    'published price — a quote-only service, an add-on that is a band, a floor or a ' +
    'quote — is refused by name rather than left out of the total, because a total ' +
    'that quietly dropped a line is a number somebody would send to a client.');
/* -------------------------------------------------------------- the rate card */
export const ExtraUnitSchema = z
    .object({
    code: z.string().describe('The unit, by code — "article", "hour", "spend_band".'),
    noun: z.string().describe('One of them, singular — "article".'),
    noun_plural: z.string().describe('More than one — "articles".'),
    unit_amount: MoneySchema.describe('USD, Tier 1, per unit, before rounding to $5.'),
    valid_from: DateOnlySchema.describe('The day this price took effect.'),
})
    .describe('One thing a client can buy past a block’s allowance, at the rate card price.');
export const AddOnSchema = z
    .object({
    code: CatalogCodeSchema,
    name: z.string().describe('What it reads as on a quote.'),
    pricing: AddOnPricingSchema,
    amount: MoneySchema.nullable().describe('USD Tier 1 for "fixed", the floor for "from", the low end for "range", null for ' +
        '"quote" and "percent".'),
    amount_max: MoneySchema.nullable().describe('The high end of a "range". Null otherwise.'),
    percent_of_template_bps: z
        .int()
        .nullable()
        .describe('Basis points of the template amount for "percent" — 2500 is +25%. Null else.'),
    per_unit: z
        .boolean()
        .describe('True when it is bought a quantity of — an extra page, a connector.'),
    excludes: z
        .array(CatalogCodeSchema)
        .describe('Add-ons this one replaces and cannot sit beside — Odoo Accounting excludes ' +
        'Invoicing. Asking for both is refused.'),
    note: z
        .string()
        .nullable()
        .describe('What the rate card prints beside it — "per shop", "localisation dependent".'),
    active: z
        .boolean()
        .describe('False when it is no longer offered. Quotes that already carry it keep it.'),
})
    .describe('One line a project quote can carry beyond its template.');
export const ProjectTemplateSchema = z
    .object({
    code: CatalogCodeSchema,
    service: CatalogCodeSchema,
    size: ProjectSizeSchema,
    name: z.string().describe('What it is called on the grid — "Business site, up to 6 pages".'),
    scope: z.string().describe('What it covers, in a sentence.'),
    base_amount: MoneySchema.describe('USD, Tier 1.'),
    add_ons: z
        .array(AddOnSchema)
        .describe('Add-ons that belong to this template alone — the Odoo components.'),
    active: z
        .boolean()
        .describe('False when it is no longer sold. Work orders that quote it keep it.'),
})
    .describe('One project template.');
export const RateCardServiceSchema = z
    .object({
    code: CatalogCodeSchema,
    name: z.string().describe('What it is called — "SEO content".'),
    category: ServiceCategorySchema,
    block_scope: z.string().describe('What one block covers, in the words PRICING.md uses.'),
    allowance_unit: z
        .string()
        .nullable()
        .describe('What the allowance is counted in — "article", "credit", "hour" — or null.'),
    allowance_qty: z
        .int()
        .nullable()
        .describe('How many of them one block includes, or null when it is not metered.'),
    replaces_service: CatalogCodeSchema.nullable().describe('The service this one replaces rather than stacks with — Max replaces Base, Odoo ' +
        'Plus replaces Odoo Base. Null when it stacks with everything.'),
    sold_as_block_only: z
        .boolean()
        .describe('True when there is nothing to buy past the allowance — the card shows "—".'),
    metered: z.boolean().describe('True when delivery draws down an allowance.'),
    block_amount: MoneySchema.nullable().describe('USD, Tier 1, per block per month. Null means quote-only — never zero.'),
    quote_only: z.boolean().describe('True when there is no published price at all.'),
    valid_from: DateOnlySchema.nullable().describe('The day the current block price took effect, or null when there is no price.'),
    extra_units: z.array(ExtraUnitSchema).describe('What can be bought past its allowance.'),
    templates: z.array(ProjectTemplateSchema).describe('Its project templates, for a project.'),
    add_ons: z
        .array(AddOnSchema)
        .describe('Add-ons shared by all of its templates — the website add-ons.'),
    active: z.boolean().describe('False when it is no longer sold. Existing retainers keep it.'),
})
    .describe('One service on the rate card, with everything priced off it.');
export const WidgetTypeSchema = z
    .object({
    code: CatalogCodeSchema,
    name: z.string().describe('What it is called — "Support chatbot".'),
    unit_noun: z.string().describe('What one billable event is — "conversation", "booking".'),
    unit_amount: MoneySchema.describe('USD per unit. Snapshotted onto every event it bills. Zero for a kind that is not ' +
        'charged per unit, which then has no allowance and no cap.'),
    monthly_amount: MoneySchema.describe('USD per month, whatever the widget does — zero for a kind sold only by use, or a free ' +
        'one. Snapshotted onto every widget at creation; billed in arrears for each full ' +
        'calendar month a widget is live, so its first partial month is free.'),
    default_included: z
        .int()
        .describe('Units included in a month before anything is billed. 0 with no unit price.'),
    default_cap: z
        .int()
        .describe('The default monthly ceiling. Past it, events are recorded and never billed. 0 with ' +
        'no unit price.'),
    fallback_text: z
        .string()
        .describe('What the site shows once the cap is reached, in the client’s own words.'),
    pilot: z.boolean().describe('True while the unit price is provisional. The portal says so.'),
    active: z.boolean().describe('False when the kind is retired. Installed widgets keep running.'),
    config_schema: z
        .record(z.string(), z.unknown())
        .nullable()
        .describe('The JSON Schema a widget of this kind is configured with, or null when the kind ' +
        'takes no configuration. Snapshotted onto every widget at creation, so changing ' +
        'it here reaches new widgets only.'),
    hosted_by: HostedSurfaceSchema.nullable().describe(`${HostedSurfaceSchema.description ?? ''} Snapshotted onto every widget at creation, ` +
        'and fixed once a widget of the kind exists.'),
})
    .describe('One kind of widget, and what a use of it costs.');
export const ProjectPresetSchema = z
    .object({
    code: CatalogCodeSchema,
    name: z.string().describe('"Sales starter", "Business", "Operations".'),
    template: CatalogCodeSchema.describe('The template it builds on.'),
    add_ons: z
        .array(CatalogCodeSchema)
        .describe('The add-on codes, one entry per occurrence — Operations lists the standard ' +
        'connector twice, because it includes two.'),
})
    .describe('A named project bundle. An output of the component list, not a price.');
export const PackageSchema = z
    .object({
    code: CatalogCodeSchema,
    name: z.string().describe('"Presence", "Growth", "Scale".'),
    is_template: z
        .boolean()
        .describe('True for a named preset; false for one built for a single client.'),
    items: z
        .array(z
        .object({
        service: CatalogCodeSchema,
        blocks: z.int().describe('How many blocks of it.'),
    })
        .describe('One line of the package.'))
        .describe('The blocks in it.'),
    active: z
        .boolean()
        .describe('False when it is no longer offered. Retainers on it are unaffected.'),
})
    .describe('A named bundle of monthly blocks.');
/**
 * One row of a price history: `catalog_list_prices` answers with every row a service
 * has ever had, not only the one in force, because "why is this retainer at that
 * number" is a question about a day in the past.
 */
export const PriceHistorySchema = z
    .object({
    valid_from: DateOnlySchema.describe('The day it took effect.'),
    block_amount: MoneySchema.nullable().describe('USD, Tier 1. Null is quote-only.'),
    backdated: z
        .boolean()
        .describe('True when the row was written for a day that had already passed.'),
    current: z.boolean().describe('True for the one in force today.'),
})
    .describe('One effective-dated block price.');
export const ExtraUnitHistorySchema = z
    .object({
    code: z.string().describe('The unit, by code.'),
    noun: z.string().describe('One of them, singular.'),
    noun_plural: z.string().describe('More than one.'),
    unit_amount: MoneySchema.describe('USD, Tier 1, per unit.'),
    valid_from: DateOnlySchema.describe('The day it took effect.'),
    backdated: z
        .boolean()
        .describe('True when the row was written for a day that had already passed.'),
    current: z.boolean().describe('True for the one in force today.'),
})
    .describe('One effective-dated extra-unit price.');
export const RateCardSchema = z
    .object({
    at: DateOnlySchema.describe('The day this card is effective on. Prices are effective-dated.'),
    services: z.array(RateCardServiceSchema).describe('Every service, by code.'),
    tiers: z
        .array(z
        .object({
        code: TierCodeSchema,
        multiplier_bps: z.int().describe('10000, 7000 or 5000.'),
        markets: z
            .array(z.string())
            .describe('Illustrative markets. Tier is set per client in admin, not from this.'),
    })
        .describe('One market tier.'))
        .describe('The three market tiers (PRICING.md).'),
    bundle_discounts: z
        .array(z
        .object({
        min_blocks: z.int().describe('The band starts here.'),
        bps: z.int().describe('Basis points off the list total: 0, 500, 1000, 1500.'),
    })
        .describe('One bundle-discount band.'))
        .describe('The bundle discount, as bands. The deepest band the package reaches wins.'),
    content_types: z
        .array(z
        .object({
        code: z.enum(CONTENT_TYPE_CODES).describe('reel, carousel, static or story.'),
        credits: z.int().describe('What one piece costs against the content allowance.'),
        extra_unit: z.string().describe('The extra-unit code priced when it is past it.'),
    })
        .describe('One kind of content piece.'))
        .describe('Content credits (PRICING.md, Content types).'),
    project_presets: z
        .array(ProjectPresetSchema)
        .describe('The Odoo presets (PRICING.md, Odoo › Presets).'),
    packages: z
        .array(PackageSchema)
        .describe('Packages. A preset is a template package; nothing else distinguishes them.'),
    widget_types: z.array(WidgetTypeSchema).describe('Every widget type and its unit price.'),
})
    .describe('The rate card as it stands today: every service, its block price and its extra units, ' +
    'the tiers, the bundle discount, the content credits, the project templates and ' +
    'add-ons, the packages and the widget types. Every amount is USD at Tier 1 — apply ' +
    'the tier and the currency with catalog_compute_price rather than by hand.');
/* ---------------------------------------------------- the card a client sees */
/**
 * The rate card at one client's tier and currency, grouped the way
 * `client/billing.html` §3–§5 groups it.
 *
 * Every amount here is **already** at the client's tier and in their currency, and
 * rounded by the same rule billing rounds by (`pricing.displayPrice`), so what the
 * client reads is what they would be charged. A `null` amount is a quote, never a
 * zero — the page prints "Quote", and `sold_as_block_only` prints "—" for overage.
 */
export const ClientCatalogSchema = z
    .object({
    at: DateOnlySchema.describe('The day the prices were read.'),
    tier: TierCodeSchema,
    currency: CurrencySchema,
    monthly: z
        .array(z
        .object({
        code: CatalogCodeSchema,
        name: z.string().describe('What it is called.'),
        block_scope: z.string().describe('What one block covers.'),
        included: z
            .string()
            .describe('What one block includes, as a phrase — "4 articles", "60 credits", "1 ' +
            'platform". "Not metered." when nothing is counted.'),
        allowance_unit: z.string().nullable(),
        allowance_qty: z.int().nullable(),
        overage: z
            .array(z
            .object({
            code: z.string().describe('The unit, by code.'),
            noun: z.string().describe('One of them, as the page prints it.'),
            unit_amount: MoneySchema.describe('At this client’s tier and currency.'),
        })
            .describe('One unit past the allowance.'))
            .describe('Empty for a block-only service, which the page renders as "—".'),
        sold_as_block_only: z
            .boolean()
            .describe('True when there is nothing to buy past the block. The page prints "—".'),
        replaces_service: CatalogCodeSchema.nullable().describe('The block this one replaces rather than stacks with, or null.'),
        price: MoneySchema.nullable().describe('Per month, at this tier. Null is a quote.'),
    })
        .describe('One monthly block, as the page prints its row.'))
        .describe('The monthly card — every block, its allowance and its overage prices.'),
    projects: z
        .array(z
        .object({
        code: CatalogCodeSchema,
        name: z.string().describe('What the project service is called.'),
        sizes: z
            .array(z
            .object({
            code: CatalogCodeSchema,
            size: ProjectSizeSchema,
            name: z.string().describe('What this size is called on the grid.'),
            price: MoneySchema.describe('At this tier and currency.'),
        })
            .describe('One size of this project.'))
            .describe('S, M and L, or the fixed templates.'),
        add_ons: z
            .array(z
            .object({
            code: CatalogCodeSchema,
            name: z.string().describe('What the add-on reads as on the card.'),
            pricing: AddOnPricingSchema,
            price: MoneySchema.nullable().describe('Null for percent and quote.'),
            price_max: MoneySchema.nullable().describe('The top of a range.'),
            percent_of_template_bps: z.int().nullable(),
            note: z.string().nullable(),
        })
            .describe('One add-on row.'))
            .describe('Everything that can be added, at this tier.'),
    })
        .describe('One project service and its grid.'))
        .describe('Project work — never a monthly line (client/billing.html §4).'),
    widgets: z
        .array(z
        .object({
        code: CatalogCodeSchema,
        name: z.string().describe('What the widget is called.'),
        unit_noun: z.string().describe('One billable event — "conversation", "booking".'),
        unit_amount: MoneySchema.describe('Per unit, in this client’s currency. Not tiered: a widget unit price is ' +
            'agreed when the widget goes live and snapshotted onto every event. Zero ' +
            'for a kind not charged per unit.'),
        monthly_amount: MoneySchema.describe('Per month, in this client’s currency, untiered like the unit price — zero ' +
            'for a kind sold only by use, or a free one. Billed in arrears from a ' +
            'widget’s first full month live.'),
        default_included: z.int().describe('Units a month included before anything bills.'),
        default_cap: z
            .int()
            .describe('The default ceiling. Past it, use is recorded, never billed.'),
        pilot: z.boolean().describe('True while the unit price is provisional.'),
    })
        .describe('One widget type.'))
        .describe('Widgets — billed on use, by the month, or free, always in arrears (client/billing.html ' +
        '§5, PRICING.md §Widgets).'),
})
    .describe('The rate card at this client’s market tier and billing currency.');
//# sourceMappingURL=catalog.js.map